import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/okkio";
import { metaPurchaseOnce } from "@/lib/metaPixel";
import NoIndex from "@/components/NoIndex";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("paid");
          metaPurchaseOnce(sessionId || data.token);
          setTimeout(() => navigate(`/report/${data.token}`), 1200);
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current > 12) {
          setState("failed"); return;
        }
        attempts.current += 1;
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current > 12) { setState("failed"); return; }
        attempts.current += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center bg-[#F7F9FC]">
      <NoIndex />
      <div data-testid="payment-status" className="max-w-sm">
        {state === "checking" && (<>
          <Loader2 className="w-10 h-10 text-slate-400 animate-spin mx-auto mb-5" />
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Confermiamo il pagamento…</h1>
          <p className="mt-2 text-slate-500">Un istante, stiamo sbloccando il tuo report.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-5" strokeWidth={1.75} />
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Pagamento riuscito</h1>
          <p className="mt-2 text-slate-500">Ti stiamo portando al report completo…</p>
        </>)}
        {state === "failed" && (<>
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-5" strokeWidth={1.75} />
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Pagamento non confermato</h1>
          <p className="mt-2 text-slate-500">Se hai completato il pagamento, riprova tra poco dal link del report.</p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full font-semibold bg-[#3157F6] hover:bg-[#2444D8]">Torna alla home</Button>
        </>)}
      </div>
    </div>
  );
}
